import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styled, { useTheme } from "styled-components/macro";
import { TextArea } from "../TextArea/TextArea";
import { useFormWrapper } from "../../util/util";
import { SendUpIcon } from "../Icons/Icons";

const FormRow = styled.form`
  display: flex;
  align-items: flex-end;
  gap: ${({ theme }) => theme.resolve("spacing_lg")};
  width: 100%;
`;

const StopButton = styled.button`
  position: absolute;
  right: calc(
    ${({ theme }) => theme.resolve("spacing_md")} +
      ${({ theme }) => theme.resolve("border_md")}
  );
  top: 50%;
  transform: translateY(-50%);
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 50%;
  background: ${({ theme }) => theme.primaryTextColor};
  color: ${({ theme }) => theme.primaryBG};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
  @media (pointer: coarse) {
    width: 44px;
    height: 44px;
    right: calc(
      ${({ theme }) => theme.resolve("spacing_xs")} +
        ${({ theme }) => theme.resolve("border_md")}
    );
  }
  &:focus-visible {
    outline: ${({ theme }) => theme.resolve("border_2xl")} solid
      ${({ theme }) => theme.brandColor};
    outline-offset: 2px;
  }
`;

const StopSquare = styled.span`
  width: 10px;
  height: 10px;
  background: currentColor;
  border-radius: 1px;
`;

const InputCell = styled.div<{ inputScrollHeight?: number }>`
  flex: 1;
  min-width: 0;
  position: relative;
  textarea {
    display: block;
    padding-top: 0;
    padding-bottom: 0;
    padding-left: 0;
    font-family: inherit;
    font-size: ${({ theme }) => theme.resolve("font_size_md")};
    line-height: 21px;
  }
  @media (pointer: coarse) {
    textarea {
      padding-right: 56px !important;
    }
  }
  button[class*="TextArea__SendButton"] {
    top: 50%;
    bottom: auto;
    transform: translateY(-50%);
    transition: right 0.2s ease-out;
    right: ${({ inputScrollHeight }) =>
      inputScrollHeight && inputScrollHeight > 263 ? `18px` : "8px"} !important;
    @media (pointer: coarse) {
      width: 44px;
      height: 44px;
      right: ${({ theme }) => theme.resolve("spacing_xs")} !important;
    }
  }
  div[class*="TextArea__Wrapper"] {
    box-sizing: border-box;
    display: flex;
    align-items: center;
    min-height: 52px;
    padding: ${({ theme }) => theme.resolve("spacing_lg")}
      ${({ theme }) => theme.resolve("spacing_xl")} !important;
    &:focus-within {
      border-width: ${({ theme }) => theme.resolve("border_md")};
      border-color: ${({ theme }) => theme.tertiaryBorder};
      box-shadow: inset 0 0 0 ${({ theme }) => theme.resolve("border_md")}
        ${({ theme }) => theme.tertiaryBorder};
    }
  }
`;

export type SendMessageProps = {
  onSubmit: (message: string) => void | Promise<void>;
  inProgress: boolean;
  onStop?: () => void;
  stopping?: boolean;
};

export function SendMessage({
  onSubmit,
  inProgress,
  onStop,
  stopping,
}: SendMessageProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const { register, formState, handleSubmit, setValue } = useFormWrapper({});
  const [text, setText] = useState<string>("");
  const inputEl = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    register({ name: "message", required: true });
  }, [register]);

  const submit = (data: { message: string }) => {
    const trimmed = (data.message || "").trim();
    if (!trimmed || inProgress) return;
    setValue("message", "");
    setText("");
    onSubmit(trimmed);
  };

  const hasMessage = text.trim().length > 0;
  const handleInlineSend = () => {
    submit({ message: text });
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key !== "Enter") return;
    if (e.shiftKey) return;
    e.preventDefault();
    if (inProgress) return;
    submit({ message: e.currentTarget.value });
  };

  return (
    <FormRow noValidate onSubmit={handleSubmit(submit)}>
      <InputCell inputScrollHeight={inputEl.current?.scrollHeight}>
        <TextArea
          name={"message"}
          label={t("Message")}
          hideFloatingLabel={true}
          placeholderText={t("Ask Virtual Chemist a question")}
          theref={inputEl}
          formState={formState}
          disabled={inProgress}
          onKeyDown={handleKeyDown}
          onChange={(e: React.FormEvent<HTMLTextAreaElement>) => {
            const v = e.currentTarget.value;
            setText(v);
            setValue("message", v);
          }}
          autoGrow
          minRows={1}
          maxRows={15}
          required={true}
          value={text}
          onSend={hasMessage && !inProgress ? handleInlineSend : undefined}
          sendDisabled={!hasMessage || inProgress}
          sendLoading={inProgress}
          sendIcon={
            <SendUpIcon
              width={16}
              height={16}
              stroke={theme.primaryButtonTextColor}
            />
          }
          style={{ resize: "none", paddingRight: "45px" }}
        />
        {inProgress && onStop && (
          <StopButton
            type="button"
            onClick={onStop}
            disabled={stopping}
            aria-label={stopping ? t("Stopping…") : t("Stop")}
            title={stopping ? t("Stopping…") : t("Stop")}
          >
            <StopSquare aria-hidden="true" />
          </StopButton>
        )}
      </InputCell>
    </FormRow>
  );
}
